import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Edit2, Trash2, UserCircle, Shield, Mail, Phone,
  ChevronLeft, ChevronRight, X, Save, Eye, EyeOff, Check
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ALL_PERMISSIONS = [
  { key: 'dashboard', label: 'Dashboard', group: 'General' },
  { key: 'products_view', label: 'Ver Productos', group: 'Productos' },
  { key: 'products_edit', label: 'Editar Productos', group: 'Productos' },
  { key: 'products_create', label: 'Crear Productos', group: 'Productos' },
  { key: 'products_delete', label: 'Eliminar Productos', group: 'Productos' },
  { key: 'settings_view', label: 'Ver Configuración', group: 'Configuración' },
  { key: 'settings_edit', label: 'Editar Configuración', group: 'Configuración' },
  { key: 'clients_view', label: 'Ver Clientes', group: 'Clientes' },
  { key: 'clients_edit', label: 'Editar Clientes', group: 'Clientes' },
  { key: 'quotes_view', label: 'Ver Presupuestos', group: 'Presupuestos' },
  { key: 'quotes_edit', label: 'Editar Presupuestos', group: 'Presupuestos' },
  { key: 'projects_view', label: 'Ver Proyectos', group: 'Proyectos' },
  { key: 'projects_edit', label: 'Editar Proyectos', group: 'Proyectos' },
  { key: 'projects_create', label: 'Crear Proyectos', group: 'Proyectos' },
  { key: 'analytics_view', label: 'Ver Analytics', group: 'Analytics' },
  { key: 'users_view', label: 'Ver Usuarios', group: 'Usuarios' },
  { key: 'users_edit', label: 'Editar Usuarios', group: 'Usuarios' },
  { key: 'users_create', label: 'Crear Usuarios', group: 'Usuarios' },
];

const UserModal = ({ user, onClose, onSave }) => {
  const [formData, setFormData] = useState(user || {
    email: '',
    full_name: '',
    password: '',
    role: 'empleado',
    is_active: true,
    phone: '',
    department: '',
    permissions: {
      dashboard: true,
      products_view: true, products_edit: false, products_create: false, products_delete: false,
      settings_view: true, settings_edit: false,
      clients_view: true, clients_edit: false,
      quotes_view: true, quotes_edit: false,
      projects_view: true, projects_edit: false, projects_create: false,
      analytics_view: true,
      users_view: false, users_edit: false, users_create: false
    }
  });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const handlePermissionChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: value }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  const permissionGroups = ALL_PERMISSIONS.reduce((acc, perm) => {
    if (!acc[perm.group]) acc[perm.group] = [];
    acc[perm.group].push(perm);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">
            {user ? 'Editar Usuario' : 'Nuevo Usuario'}
          </h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Datos básicos */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-emerald-400">Información del Usuario</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Nombre Completo *</label>
                <input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={!!user}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500 disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">
                  Contraseña {user ? '(dejar vacío para no cambiar)' : '*'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!user}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 pr-10 text-white focus:outline-none focus:border-emerald-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Teléfono</label>
                <input
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Departamento</label>
                <input
                  value={formData.department || ''}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  placeholder="Ej: Ventas, Instalaciones"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Rol</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                >
                  <option value="empleado">Empleado</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 rounded border-zinc-600 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-zinc-900"
                />
                <span className="text-sm text-zinc-300">Usuario activo</span>
              </label>
            </div>
          </div>

          {/* Permisos (solo si es empleado) */}
          {formData.role === 'empleado' && (
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-emerald-400 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Permisos del Usuario
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Object.entries(permissionGroups).map(([group, perms]) => (
                  <div key={group} className="bg-zinc-800/50 rounded-lg p-3">
                    <h5 className="text-xs font-medium text-zinc-400 uppercase mb-2">{group}</h5>
                    <div className="space-y-2">
                      {perms.map(perm => (
                        <label key={perm.key} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.permissions[perm.key] || false}
                            onChange={(e) => handlePermissionChange(perm.key, e.target.checked)}
                            className="w-4 h-4 rounded border-zinc-600 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-zinc-800"
                          />
                          <span className="text-sm text-zinc-300">{perm.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {formData.role === 'admin' && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
              <p className="text-sm text-emerald-400 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Los administradores tienen acceso completo a todas las funciones.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-zinc-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-zinc-400 hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const UsersPage = () => {
  const { fetchWithAuth, hasPermission, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const canCreate = hasPermission('users_create');
  const canEdit = hasPermission('users_edit');

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth('/api/auth/users');
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error cargando usuarios:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleSaveUser = async (userData) => {
    try {
      const url = editingUser 
        ? `/api/auth/users/${editingUser.id}`
        : '/api/auth/register';
      
      const res = await fetchWithAuth(url, {
        method: editingUser ? 'PUT' : 'POST',
        body: JSON.stringify(userData)
      });

      if (res.ok) {
        setShowModal(false);
        setEditingUser(null);
        loadUsers();
      } else {
        const error = await res.json();
        alert(error.detail || 'Error al guardar usuario');
      }
    } catch (error) {
      alert('Error de conexión');
    }
  };

  const handleDelete = async (userId) => {
    if (userId === currentUser?.id) {
      alert('No puedes eliminarte a ti mismo');
      return;
    }
    if (!confirm('¿Estás seguro de eliminar este usuario?')) return;
    
    try {
      const res = await fetchWithAuth(`/api/auth/users/${userId}`, { method: 'DELETE' });
      if (res.ok) {
        loadUsers();
      } else {
        const error = await res.json();
        alert(error.detail || 'Error al eliminar');
      }
    } catch (error) {
      alert('Error de conexión');
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Usuarios</h1>
          <p className="text-zinc-400 text-sm">Gestión de empleados y administradores</p>
        </div>
        {canCreate && (
          <button
            onClick={() => { setEditingUser(null); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
            data-testid="add-user-btn"
          >
            <Plus className="w-4 h-4" />
            Nuevo Usuario
          </button>
        )}
      </div>

      {/* Users Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          <UserCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No hay usuarios</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map((user) => (
            <div 
              key={user.id} 
              className={`bg-zinc-900/50 border rounded-xl p-5 ${
                user.id === currentUser?.id ? 'border-emerald-500/50' : 'border-zinc-800'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-zinc-800 rounded-full flex items-center justify-center">
                    <UserCircle className="w-8 h-8 text-zinc-400" />
                  </div>
                  <div>
                    <p className="font-medium text-white">{user.full_name}</p>
                    <span className={`inline-block px-2 py-0.5 text-xs rounded-full ${
                      user.role === 'admin' 
                        ? 'bg-emerald-500/20 text-emerald-400' 
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {user.role === 'admin' ? 'Administrador' : 'Empleado'}
                    </span>
                  </div>
                </div>
                {user.id === currentUser?.id && (
                  <span className="text-xs bg-zinc-700 text-zinc-300 px-2 py-1 rounded">Tú</span>
                )}
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-zinc-400">
                  <Mail className="w-4 h-4" />
                  <span className="truncate">{user.email}</span>
                </div>
                {user.phone && (
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Phone className="w-4 h-4" />
                    <span>{user.phone}</span>
                  </div>
                )}
                {user.department && (
                  <p className="text-zinc-500 text-xs">Departamento: {user.department}</p>
                )}
              </div>

              <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-800">
                <span className={`flex items-center gap-1 text-xs ${user.is_active ? 'text-emerald-400' : 'text-red-400'}`}>
                  <span className={`w-2 h-2 rounded-full ${user.is_active ? 'bg-emerald-400' : 'bg-red-400'}`}></span>
                  {user.is_active ? 'Activo' : 'Inactivo'}
                </span>
                <div className="flex gap-2">
                  {canEdit && (
                    <button
                      onClick={() => { setEditingUser(user); setShowModal(true); }}
                      className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                  {currentUser?.role === 'admin' && user.id !== currentUser?.id && (
                    <button
                      onClick={() => handleDelete(user.id)}
                      className="p-2 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <UserModal
          user={editingUser}
          onClose={() => { setShowModal(false); setEditingUser(null); }}
          onSave={handleSaveUser}
        />
      )}
    </div>
  );
};

export default UsersPage;
